import React, { Fragment, useState, useEffect } from "react";
import { ThemeProvider } from "styled-components";
import { interiorTheme } from "common/theme/interior";
import Layout from "../containers/Home/Layout";
import Fade from "react-reveal/Fade";
import "common/assets/css/main-page.css";
import Seo from "components/seo";
import AboutWrapper from "../containers/Home/about.style";
import GoogleReviews from "containers/Home/GoogleReviews";
import CallToAction from "containers/Home/CallToAction";
import CountUp from "react-countup";

const parseStatValue = (str) => {
  if (!str) return { end: 0, suffix: '', separator: '' };
  
  // Clean string and find suffix (e.g. '+')
  const suffixMatch = str.match(/([+\s]*)$/);
  const suffix = suffixMatch ? suffixMatch[0].trim() : '';
  
  // Check if there's a comma in the string
  const hasComma = str.includes(',');
  
  // Extract digits
  const numericString = str.replace(/[^0-9]/g, '');
  const end = parseInt(numericString, 10) || 0;
  
  return {
    end,
    suffix,
    separator: hasComma ? ',' : ''
  };
};

const About = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://radianceadmin.manageprojects.in/api/aboutus"
        );
        const result = await response.json();
        if (result.success) {
          setData(result.data[0]);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading data</div>;
  if (!data) return <div>No data available</div>;

  const seoData = {
    title: data.seo_title || "Radiance Hair Studio - Non Surgical Hair Replacement Expert",
    description: data.seo_description || "Radiance Hair Studio: Your non-surgical hair replacement expert. Discover natural-looking solutions to hair loss with our skilled professionals.",
    keywords: data.seo_keywords || ["About Radiance Hair Studio"],
    canonical: "https://www.radiancehairstudio.com/about.php",
  };

  const schemaData = data.schema_data ? JSON.parse(data.schema_data) : {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": "AboutPage",
    "additionalType": "https://www.radiancehairstudio.com/about.php",
    "alternativeHeadline": "Radiance Hair Studio",
    "citation": "Luxury Hair Studio where you will feel unique and special",
    "description": "Radiance Hair Studio: Your non-surgical hair replacement expert. Discover natural-looking solutions to hair loss with our skilled professionals.",
    "material": "Hair Wigs and Hair Patches",
    "image": [
      "https://www.radiancehairstudio.com/images/about-image-1.jpg",
      "https://www.radiancehairstudio.com/images/about-image-2.jpg",
      "https://www.radiancehairstudio.com/images/vision-mission.jpg",
      "https://www.radiancehairstudio.com/home/review.webp",
      "https://www.radiancehairstudio.com/home/home-image-18.webp"
    ]
  };

  return (
    <ThemeProvider theme={interiorTheme}>
      <Fragment>
        <Seo {...seoData} schemaData={schemaData} />

        <Layout>
          <AboutWrapper>
            <div className="about-us-body">
              <main className="about-us-page">
                <section className="title-bar" style={{ backgroundImage: `url(${data.aboutbanner})` }}>
                  <div className="title-bar-overlay"></div>
                  <div className="section-container">
                    <h1 className="title-bar-heading">{data.banner_title || "About Us"}</h1>
                    <h2 className="title-bar-breadcrumb" style={{ color: "#ffffff" }}>
                      Home / {data.banner_title || "About Us"}
                    </h2>
                  </div>
                </section>

                <section className="title-bar-separator">
                  <img className="separator-image" alt="" src="/images/separator.png" />
                </section>

                <section>
                  <div className="myContainer">
                    <div className="row">
                      <div className="col-md-6">
                        <Fade left>
                          <p className="subhead">
                            {data.aboutcontent_sub_heading}
                          </p>
                          <div dangerouslySetInnerHTML={{ __html: data.aboutcontent_left }} />
                        </Fade>
                        <img
                          className="areaImg full-width mb-4"
                          src={data.aboutimg_left}
                          alt="hair wig for women"
                        />
                      </div>
                      <div className="col-md-6">
                        <img
                          className="areaImg full-width mb-4"
                          src={data.aboutimg_right}
                          alt="wig shop near me"
                        />
                        <div dangerouslySetInnerHTML={{ __html: data.aboutcontent_right }} />
                      </div>
                    </div>
                  </div>
                </section>

                <section className="about-stats-section">
                  <div className="myContainer">
                    <div className="about-stats-card">
                      <div className="about-stats-card-overlay">
                        {/* Left: Stats + Features */}
                        <div className="about-stats-content">
                          {/* Stats Row */}
                          <Fade bottom>
                            <div className="stats-row">
                              <div className="stat-item">
                                <span className="stat-number">
                                  {(() => {
                                    const { end, suffix, separator } = parseStatValue(data.year_experience);
                                    return (
                                      <CountUp
                                        start={0}
                                        end={end}
                                        duration={2}
                                        separator={separator}
                                        suffix={suffix}
                                        enableScrollSpy
                                        scrollSpyOnce
                                      />
                                    );
                                  })()}
                                </span>
                                <span className="stat-label">Years of Experience</span>
                              </div>
                              <div className="stat-divider"></div>
                              <div className="stat-item">
                                <span className="stat-number">
                                  {(() => {
                                    const { end, suffix, separator } = parseStatValue(data.happy_customer);
                                    return (
                                      <CountUp
                                        start={0}
                                        end={end}
                                        duration={2}
                                        separator={separator}
                                        suffix={suffix}
                                        enableScrollSpy
                                        scrollSpyOnce
                                      />
                                    );
                                  })()}
                                </span>
                                <span className="stat-label">Happy Customers</span>
                              </div>
                              <div className="stat-divider"></div>
                              <div className="stat-item">
                                <span className="stat-number">
                                  {(() => {
                                    const { end, suffix, separator } = parseStatValue(data.branches);
                                    return (
                                      <CountUp
                                        start={0}
                                        end={end}
                                        duration={2}
                                        separator={separator}
                                        suffix={suffix}
                                        enableScrollSpy
                                        scrollSpyOnce
                                      />
                                    );
                                  })()}
                                </span>
                                <span className="stat-label">Branches Across India</span>
                              </div>
                            </div>
                          </Fade>

                          {/* Features Row */}
                          <div className="features-row">
                            <Fade bottom delay={100}>
                              <div className="feature-tile feature-dark">
                                <div className="feature-icon-wrap">
                                  <img src={data.professional_expertsicon} alt="hair extension in delhi" className="feature-icon" />
                                </div>
                                <h4 className="feature-title">{data.professional_experts}</h4>
                              </div>
                            </Fade>
                            <Fade bottom delay={200}>
                              <div className="feature-tile feature-gold">
                                <div className="feature-icon-wrap">
                                  <img src={data.consultationsicon} alt="hair bonding in delhi" className="feature-icon" />
                                </div>
                                <h4 className="feature-title">{data.consultations}</h4>
                              </div>
                            </Fade>
                            <Fade bottom delay={300}>
                              <div className="feature-tile feature-rose">
                                <div className="feature-icon-wrap">
                                  <img src={data.emi_availableicon} alt="hair wigs price" className="feature-icon" />
                                </div>
                                <h4 className="feature-title">{data.emi_available}</h4>
                              </div>
                            </Fade>
                          </div>
                        </div>

                        {/* Right: Background Image Panel */}
                        <div
                          className="about-stats-image-side"
                          style={{ backgroundImage: `url(${data.customerbgimg})` }}
                        ></div>
                      </div>
                    </div>

                  </div>
                </section>

                <section>
                  <div className="myContainer">
                    <div className="row">
                      <div className="col-md-6">
                        <img
                          className="full-width areaImg"
                          alt="Vision and Mission"
                          src={data.vision_missionimg}
                        />
                      </div>
                      <div className="col-md-6">
                        <div dangerouslySetInnerHTML={{ __html: data.vision_missioncontent }} />
                      </div>
                    </div>
                  </div>
                </section>
              </main>
            </div>
          </AboutWrapper>

          <GoogleReviews />
          <CallToAction />
        </Layout>
      </Fragment>
    </ThemeProvider>
  );
};

export default About;